import chalk from 'chalk';

/**
 * Directories code migrations must never walk. Without this, patterns like
 * `** /web/** /package.json` match vendored files under `node_modules` — the
 * migration then rewrites (and drops a `.bak` beside) files npm blows away on the
 * next install, and the scan needlessly walks hundreds of files (the historical
 * "584 skipped" noise). bin/obj/dist are build output; .git is repo metadata.
 */
export const MIGRATION_IGNORE_GLOBS = [
  '**/node_modules/**',
  '**/bin/**',
  '**/obj/**',
  '**/dist/**',
  '**/.git/**',
];

/** The subset of upgrade results that decides the final outcome. */
export interface UpgradeFailureState {
  /** True when a `dotnet restore` failed — the .csproj was edited but won't restore. */
  restoreFailed: boolean;
  /** Count of SmartStack package upgrades that errored. */
  nugetFailed: number;
  /** Count of other-package upgrades that errored (--all-packages). */
  otherPkgFailed: number;
  /** Count of required packages that failed to add. */
  requiredPkgFailed: number;
  /** Missing Program.cs bootstrap calls (a warning, not a hard failure). */
  programCsIssues: string[];
}

export interface UpgradeOutcome {
  /** True when a package add or restore failed — the project build may be broken. */
  hasFailures: boolean;
  /** Process exit code: 1 when the upgrade left the project in a broken state. */
  exitCode: number;
  /** Box rendering style for the final summary. */
  boxStyle: 'success' | 'warning' | 'error';
  /** Headline shown at the top of the final summary box. */
  title: string;
}

/**
 * Pure decision: given what actually happened, what should the final summary say and
 * what exit code should the process carry. A failed restore / package add is a HARD
 * failure (red box, exit 1) so `ss upgrade` never reports green over a broken build —
 * critical for CI and worktree-driven runs. A missing Program.cs call is a warning
 * (exit 0): the upgrade succeeded, the user just has wiring to finish.
 */
export function computeUpgradeOutcome(result: UpgradeFailureState): UpgradeOutcome {
  const hasHardFailures =
    result.restoreFailed ||
    result.nugetFailed > 0 ||
    result.otherPkgFailed > 0 ||
    result.requiredPkgFailed > 0;

  if (hasHardFailures) {
    return {
      hasFailures: true,
      exitCode: 1,
      boxStyle: 'error',
      title: chalk.red.bold('Upgrade completed with errors'),
    };
  }

  if (result.programCsIssues.length > 0) {
    return {
      hasFailures: false,
      exitCode: 0,
      boxStyle: 'warning',
      title: chalk.yellow.bold('Upgrade completed — Program.cs needs attention'),
    };
  }

  return {
    hasFailures: false,
    exitCode: 0,
    boxStyle: 'success',
    title: chalk.green.bold('Upgrade completed!'),
  };
}

// ── Export-datasets seam ──────────────────────────────────────────────────────

/** Markers of the export-datasets seam. No scaffolder owns them, so `upgrade` carries them itself. */
export const EXPORT_DATASETS_BEGIN = '// <<< EXPORT-DATASETS-DI BEGIN >>>';
export const EXPORT_DATASETS_END = '// <<< EXPORT-DATASETS-DI END >>>';

/**
 * The block is anchored on the navigation-stats markers rather than appended before the closing brace
 * of `AddInfrastructure`: a brace scan that mis-parses would corrupt the file the whole app boots
 * from, and every project carrying one of these seams carries that anchor.
 */
const NAVIGATION_STATS_ANCHOR = '// <<< NAVIGATION-STATS-DI BEGIN >>>';

/**
 * Inserts the COMMENTED export-datasets seam into a project's `DependencyInjection.cs`.
 *
 * Nothing is ever auto-registered: an exportable dataset is an opt-in business decision, and its
 * fields, measures and pivots are not derivable from the pagespecs. All this does is teach an older
 * project that the seam exists.
 *
 * Returns `null` when there is nothing to do — the seam is already there, or the anchor is missing —
 * so the caller can tell "done" from "skipped" without re-reading the file.
 */
export function insertExportDatasetsSeam(content: string, baseNamespace: string): string | null {
  if (content.includes(EXPORT_DATASETS_BEGIN)) return null;
  if (!content.includes(NAVIGATION_STATS_ANCHOR)) return null;

  // The anchor sits at 8 spaces and `replace` lands right after them, so the FIRST line inherits
  // them, every following line carries them explicitly, and a trailing blank line plus one indent
  // hands the anchor back the column it was on.
  const indent = ' '.repeat(8);
  const block = [
    '// ── Exportable datasets (your data in the HR export wizard) ────────────────',
    '// Register an IExportDatasetProvider and your entities appear as a SOURCE in',
    "// /hr/export, mergeable with the platform's own on the same row of the same",
    '// month — with ZERO client frontend code. Rows must key on a USER id; identity',
    '// and period columns belong to the ENGINE. The engine also resolves the "Qui ?"',
    '// targeting and hands you the union in context.UserIds — never re-filter on',
    '// context.DepartmentIds. See references/export-datasets.md.',
    EXPORT_DATASETS_BEGIN,
    `// services.AddSmartStackExportDatasetProvider<${baseNamespace}.Application.Exports.MyExportProvider>();`,
    EXPORT_DATASETS_END,
  ].join(`\n${indent}`);

  return content.replace(NAVIGATION_STATS_ANCHOR, `${block}\n\n${indent}${NAVIGATION_STATS_ANCHOR}`);
}

// ── Platform-admin fallback block (aggregate-component-registry ≤ 5.12) ───────

/**
 * Header line of the block `aggregate-component-registry` used to prepend to EVERY generated
 * `componentRegistry.generated.ts`. Anchored on the header rather than reconstructed from the
 * key list, so a project generated by any CLI ≤ 5.12 is recognised verbatim.
 */
export const ADMIN_FALLBACK_HEADER = '// ─── Platform admin fallbacks (auto-generated)';

/** Module specifier of the placeholder component the block imported. */
export const ADMIN_FALLBACK_SPECIFIER = '@/components/AdminModuleFallback';

const ADMIN_REGISTER_RE =
  /^PageRegistry\.register\('administration(?:\.[a-z0-9-]+)?',\s*AdminModuleFallback\);\s*$/;

/**
 * Strips the platform-admin fallback block from a generated `componentRegistry.generated.ts`.
 *
 * WHY: the block registered a placeholder on `administration` and its nine module componentKeys.
 * A registered component ALWAYS wins over the SDK's generic `ApplicationHomePage` /
 * `ModuleHomePage`, so every `/administration/*` root rendered the placeholder instead of the
 * platform's own landing page — and in apps generated before 5.12 that placeholder was a bare
 * `return null`, i.e. a BLANK page with no error of any kind. The block's stated rationale (a
 * MISSING_PAGE on bare admin URLs) never applied: `MissingComponentPage` is only reachable from a
 * SECTION or RESOURCE key.
 *
 * The removal is anchored on the header comment and ends on the LAST consecutive
 * `PageRegistry.register('administration…', AdminModuleFallback);` line — never on a brace scan,
 * and never on a line the client could have authored.
 *
 * Returns `null` when there is nothing to do (block absent, or a header with no registration under
 * it → refuse to guess the extent), so the caller can tell "done" from "skipped".
 */
export function stripPlatformAdminFallbacks(content: string): string | null {
  const eol = content.includes('\r\n') ? '\r\n' : '\n';
  const lines = content.split(/\r?\n/);

  const start = lines.findIndex((l) => l.startsWith(ADMIN_FALLBACK_HEADER));
  if (start < 0) return null;

  let end = -1;
  for (let i = start; i < lines.length; i++) {
    if (ADMIN_REGISTER_RE.test(lines[i])) end = i;
    else if (end >= 0) break; // the run of registrations is over
  }
  if (end < 0) return null;

  const before = lines.slice(0, start);
  while (before.length > 0 && before[before.length - 1].trim() === '') before.pop();
  const after = lines.slice(end + 1);
  while (after.length > 0 && after[0].trim() === '') after.shift();

  return [...before, '', ...after].join(eol);
}

/** What `upgrade` should do with an `AdminModuleFallback.tsx` left on disk. */
export type AdminFallbackVerdict = 'orphan' | 'still-referenced' | 'customised';

/**
 * Decides whether the scaffolded placeholder can be deleted.
 *
 * `customised` mirrors `scaffold-frontend-auth`'s own marker check — a client who turned the
 * placeholder into a real page keeps it, and upgrade only warns. `still-referenced` covers the
 * belt-and-braces case where the registry was NOT stripped (dry run, hand-edited file): deleting
 * then would turn a working import into a TS2307.
 */
export function classifyAdminModuleFallback(
  fileSource: string,
  otherSources: string[],
): AdminFallbackVerdict {
  const head = fileSource.trimStart().slice(0, 100);
  if (head.startsWith('/* @customised') || head.startsWith('// @customised')) return 'customised';
  const referenced = otherSources.some(
    (s) => s.includes(ADMIN_FALLBACK_SPECIFIER) || /from ['"`]\.{1,2}\/AdminModuleFallback['"`]/.test(s),
  );
  return referenced ? 'still-referenced' : 'orphan';
}
